import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '@common/apis/axios-base-query';

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled', 'canceled', 'error']);
const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 60;

export interface GeneratedAgentConfig {
  name: string;
  description: string;
  exploration_hint?: string;
  main_prompt: string;
  validation_prompt?: string;
  output_schema: Record<string, unknown>;
  ac_template: Record<string, unknown>;
}

interface AgentRunResult {
  id: string;
  status: string;
  output_data?: Record<string, unknown> | null;
  error?: string | null;
}

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

function extractJson(content: string): GeneratedAgentConfig {
  const match = content.match(/```json\s*([\s\S]*?)\s*```/);
  const jsonStr = match ? match[1] : content;
  return JSON.parse(jsonStr);
}

export interface AcGeneratorInput {
  userPrompt: string;
  currentTemplate?: string;
  jsonSchema?: string;
}

function buildAcGeneratorPrompt({
  userPrompt,
  currentTemplate,
  jsonSchema,
}: AcGeneratorInput): string {
  const lines: string[] = [
    'You are an expert at creating and editing Adaptive Card templates for the sbrain platform.',
    '',
    'An Adaptive Card template is a JSON object with this structure:',
    '{ "type": "AdaptiveCard", "version": "1.5", "body": [...] }',
    '',
    'Supported element types:',
    '- TextBlock: { type, text, size (Small|Default|Medium|Large|ExtraLarge), weight (Lighter|Default|Bolder), color (Default|Dark|Light|Accent|Good|Warning|Attention), wrap, isSubtle }',
    '- FactSet: { type, facts: [{ title, value }] }',
    '- Image: { type, url, size (Small|Medium|Large|Stretch) }',
    '- Badge: { type, text, style (Default|Good|Warning|Attention|Accent|Dark) }',
    '- BadgeSet: { type, badges: [{ text, style }] }',
    '- Container: { type, items: [...], style (default|emphasis|accent|good|attention|warning) }',
    '- ColumnSet: { type, columns: [{ type: "Column", width (auto|stretch|number), items: [...] }] }',
    '- BarChart: { type, data: [...], labels: [...] }',
    '- DonutChart: { type, data: [...], labels: [...] }',
    '- ProgressBar: { type, value, label }',
    '',
    // eslint-disable-next-line no-template-curly-in-string
    'Use ${fieldName} syntax for data binding (e.g. ${amount}, ${items[0].title}).',
    '',
  ];

  if (jsonSchema) {
    lines.push('[CONTEXT: JSON Output Schema]');
    lines.push(jsonSchema);
    lines.push('');
  }

  if (currentTemplate) {
    lines.push('[CONTEXT: Current Adaptive Card Template]');
    lines.push(currentTemplate);
    lines.push('');
  }

  lines.push('[USER REQUEST]');
  lines.push(userPrompt);
  lines.push('');
  lines.push(
    'Return ONLY a valid JSON object for the Adaptive Card template, wrapped in a ```json code block.',
  );

  return lines.join('\n');
}

export const GenerateApi = createApi({
  reducerPath: 'generateApi',
  baseQuery: axiosBaseQuery(),
  endpoints: (builder) => ({
    generateAgent: builder.mutation<GeneratedAgentConfig, string>({
      queryFn: async (task, _api, _extraOptions, baseQuery) => {
        const startRes = await baseQuery({
          pathname: '/v1/agents/agent_creation/run',
          method: 'POST',
          data: { task },
        });
        if (startRes.error) return { error: startRes.error };

        const runId = (startRes.data as AgentRunResult)?.id;
        if (!runId) {
          return { error: { status: 'CUSTOM_ERROR', error: 'No run ID returned by the server.' } };
        }

        for (let i = 0; i < MAX_POLLS; i++) {
          await sleep(POLL_INTERVAL_MS);

          const pollRes = await baseQuery({
            pathname: `/v1/agents/runs/${encodeURIComponent(runId)}`,
            method: 'GET',
          });
          if (pollRes.error) return { error: pollRes.error };

          const run = pollRes.data as AgentRunResult;
          const status = run.status?.toLowerCase() ?? '';

          if (!TERMINAL_STATUSES.has(status)) continue;

          if (status === 'failed' || status === 'error') {
            return {
              error: { status: 'CUSTOM_ERROR', error: run.error ?? 'Agent generation failed.' },
            };
          }

          const outputData = run.output_data;
          if (!outputData || Object.keys(outputData).length === 0) {
            return {
              error: { status: 'CUSTOM_ERROR', error: 'Run completed but returned no content.' },
            };
          }

          // Legacy path: agent returned {"content": "```json...```"} string
          const rawContent = outputData.content;
          if (typeof rawContent === 'string') {
            try {
              return { data: extractJson(rawContent) };
            } catch {
              return {
                error: {
                  status: 'CUSTOM_ERROR',
                  error: 'Could not parse the generated agent configuration.',
                },
              };
            }
          }

          // Structured path: agent returned the config directly as a flat dict
          return { data: outputData as unknown as GeneratedAgentConfig };
        }

        return {
          error: { status: 'CUSTOM_ERROR', error: 'Generation timed out after 2 minutes.' },
        };
      },
    }),

    generateAcTemplate: builder.mutation<Record<string, unknown>, AcGeneratorInput>({
      queryFn: async (input, _api, _extraOptions, baseQuery) => {
        const task = buildAcGeneratorPrompt(input);

        const startRes = await baseQuery({
          pathname: '/v1/agents/ac_generator/run',
          method: 'POST',
          data: { task },
        });
        if (startRes.error) return { error: startRes.error };

        const runId = (startRes.data as AgentRunResult)?.id;
        if (!runId) {
          return { error: { status: 'CUSTOM_ERROR', error: 'No run ID returned by the server.' } };
        }

        for (let i = 0; i < MAX_POLLS; i++) {
          await sleep(POLL_INTERVAL_MS);

          const pollRes = await baseQuery({
            pathname: `/v1/agents/runs/${encodeURIComponent(runId)}`,
            method: 'GET',
          });
          if (pollRes.error) return { error: pollRes.error };

          const run = pollRes.data as AgentRunResult;
          const status = run.status?.toLowerCase() ?? '';

          if (!TERMINAL_STATUSES.has(status)) continue;

          if (status === 'failed' || status === 'error') {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: run.error ?? 'AC template generation failed.',
              },
            };
          }

          const outputData = run.output_data;
          if (!outputData || Object.keys(outputData).length === 0) {
            return {
              error: { status: 'CUSTOM_ERROR', error: 'Run completed but returned no content.' },
            };
          }

          const rawContent = outputData.content;
          if (typeof rawContent === 'string') {
            try {
              return { data: extractJson(rawContent) as unknown as Record<string, unknown> };
            } catch {
              return {
                error: {
                  status: 'CUSTOM_ERROR',
                  error: 'Could not parse the generated AC template.',
                },
              };
            }
          }

          return { data: outputData as unknown as Record<string, unknown> };
        }

        return {
          error: { status: 'CUSTOM_ERROR', error: 'AC generation timed out after 2 minutes.' },
        };
      },
    }),
  }),
});

export const { useGenerateAgentMutation, useGenerateAcTemplateMutation } = GenerateApi;
